import { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import AuthContext from '../context/AuthContext';
import config from '../config';

const CreatePost = () => {
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [pgnData, setPgnData] = useState([]);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const editorRef = useRef(null);

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setTitle(newTitle);
        // Auto-generate slug
        setSlug(newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
    };

    const handlePgnUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);

        // Read PGN file client-side using FileReader
        const reader = new FileReader();

        reader.onload = (event) => {
            const pgnContent = event.target.result;
            const newPgnData = [...pgnData, pgnContent];
            setPgnData(newPgnData);
            setUploading(false);

            // Auto-insert PGN placeholder at cursor
            if (editorRef.current) {
                const pgnIndex = newPgnData.length - 1;
                editorRef.current.insertContent(`<p>[PGN:${pgnIndex}]</p>`);
            }
        };

        reader.onerror = () => {
            setError('Failed to read PGN file');
            setUploading(false);
        };

        reader.readAsText(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${config.API_URL}/blogs`,
                { title, slug, content, pgn_data: pgnData },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create post');
        }
    };

    return (
        <div className="container">
            <div className="form-container">
                <h1>Create New Post</h1>
                {error && <div className="error">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Slug</label>
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Upload PGN File (Optional)</label>
                        <input
                            type="file"
                            accept=".pgn"
                            onChange={handlePgnUpload}
                            disabled={uploading}
                        />
                        {uploading && <p>Uploading...</p>}
                        {pgnData.length > 0 && (
                            <p style={{ color: '#4CAF50' }}>
                                ✓ {pgnData.length} PGN file(s) uploaded and inserted
                            </p>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Content</label>
                        <Editor
                            apiKey='sp05u5nayc70fuope6p4gxfnd0jqcdlm46jds60xdc38sv9r'
                            onInit={(evt, editor) => editorRef.current = editor}
                            value={content}
                            onEditorChange={(newContent) => setContent(newContent)}
                            init={{
                                height: 500,
                                menubar: false,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                paste_data_images: true,
                                images_upload_handler: async (blobInfo, progress) => {
                                    try {
                                        const formData = new FormData();
                                        formData.append('image', blobInfo.blob(), blobInfo.filename());

                                        const token = localStorage.getItem('token');
                                        const response = await axios.post(`${config.API_URL}/upload/image`, formData, {
                                            headers: {
                                                'Content-Type': 'multipart/form-data',
                                                Authorization: `Bearer ${token}`
                                            },
                                            onUploadProgress: (e) => {
                                                progress(e.loaded / e.total * 100);
                                            }
                                        });

                                        return response.data.url;
                                    } catch (error) {
                                        console.error('Image upload failed:', error);
                                        throw new Error('Image upload failed: ' + (error.response?.data?.message || error.message));
                                    }
                                }
                            }}
                        />
                        <small style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                            💡 Tip: Upload a PGN file above, and it will be automatically inserted at your cursor position as [PGN:0], [PGN:1], etc.
                        </small>
                    </div>
                    <button type="submit" className="btn">Create Post</button>
                </form>
            </div>
        </div>
    );
};

export default CreatePost;
